import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MusicIcon, RotateCcwIcon, SettingsIcon } from 'lucide-react';
import { Plant } from '../components/Plant';
import { GoalLine } from '../components/GoalLine';
import { DurationSheet } from '../components/DurationSheet';
import { SoundSheet } from '../components/SoundSheet';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { MODE_LABELS } from '../data/presets';
import { Mode } from '../types/pomodoro';
import { formatClock } from '../utils/time';
import { usePomodoro } from '../contexts/PomodoroContext';
import { useAmbient } from '../contexts/AmbientContext';
import { useScreenInit } from '../useScreenInit.js';
const MODES: Mode[] = ['focus', 'short', 'long'];
export function TimerScreen({
  onOpenSettings
}: {
  onOpenSettings: () => void;
}) {
  const {
    settings,
    timer,
    remainingMs,
    totalMs,
    sessionInProgress,
    start,
    reset,
    selectMode,
    setDuration,
    task,
    setTask,
    message,
    dismissMessage,
    growthKey,
    goalReached,
    todayMinutes
  } = usePomodoro();
  const ambient = useAmbient();
  const screenInit = useScreenInit();
  const [durationOpen, setDurationOpen] = useState(Boolean(screenInit.durationOpen));
  const [soundOpen, setSoundOpen] = useState(Boolean(screenInit.soundOpen));
  const [pendingMinutes, setPendingMinutes] = useState<number | null>(null);
  const [pendingMode, setPendingMode] = useState<Mode | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  useEffect(() => {
    if (timer.status === 'running') ambient.resumeForSession();else ambient.pauseForSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer.status]);
  const primaryLabel = timer.status === 'running' ? 'Pause' : timer.status === 'paused' ? 'Resume' : 'Start';
  const applyDuration = (minutes: number) => {
    if (sessionInProgress) {
      setPendingMinutes(minutes);
      return;
    }
    setDuration(timer.mode, minutes);
    setDurationOpen(false);
  };
  const handleMode = (mode: Mode) => {
    if (mode === timer.mode) return;
    if (sessionInProgress) {
      setPendingMode(mode);
      return;
    }
    selectMode(mode);
  };
  const handleReset = () => {
    if (sessionInProgress) {
      setConfirmReset(true);
      return;
    }
    reset();
  };
  return <div className="flex min-h-full flex-col px-5 pb-24 pt-[max(1rem,env(safe-area-inset-top))]" data-id="element-163" data-mp="2xzsj4znajixs">
      <header className="flex items-center justify-between" data-id="element-164" data-mp="32zf2lqtppvf1">
        <h1 className="text-sm font-semibold tracking-tight text-ink" data-id="element-165" data-mp="37ukj2a4cihvq">Focus</h1>
        <div className="flex items-center" data-id="element-166" data-mp="3cw9w1vb4p5mg">
          <button type="button" onClick={() => setSoundOpen(true)} aria-label="Focus sounds" className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral ${ambient.playing ? 'text-coral' : 'text-muted hover:text-ink'}`} data-id="element-167" data-mp="3ct4a2oxsqjuy">
            <MusicIcon size={18} strokeWidth={1.5} data-id="element-168" data-mp="1a7ie5ed8yp0t" />
          </button>
          <button type="button" onClick={onOpenSettings} aria-label="Settings" className="flex h-11 w-11 items-center justify-center rounded-full text-muted transition-colors duration-150 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral" data-id="element-169" data-mp="15973j1f4oahj">
            <SettingsIcon size={18} strokeWidth={1.5} data-id="element-170" data-mp="1swydvzh3ydg6" />
          </button>
        </div>
      </header>

      <div className="mx-auto mt-6 flex w-full max-w-sm flex-1 flex-col items-center" data-id="element-171" data-mp="1ozk7sgoq4lse">
        <div role="group" aria-label="Session mode" className="flex items-center gap-1 rounded-full border border-line p-1" data-id="element-172" data-mp="1jy3bkyh6pni5">
          {MODES.map(mode => {
          const active = timer.mode === mode;
          return <button key={mode} aria-pressed={active} type="button" onClick={() => handleMode(mode)} className={`min-h-[36px] rounded-full px-3.5 text-xs font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral ${active ? 'bg-ink text-bg' : 'text-muted hover:text-ink'}`} data-id="element-173" data-mp="1wfvtqiik7lba">
                {MODE_LABELS[mode]}
              </button>;
        })}
        </div>

        <div className="mt-8" data-id="element-174" data-mp="265p8tthwuika">
          <Plant progress={timer.mode === 'focus' ? Math.min(1, Math.max(0, 1 - remainingMs / totalMs)) : 1} running={timer.status === 'running'} growthKey={growthKey} data-id="element-175" data-mp="266i49ybf5ln4" />
        </div>

        <button type="button" onClick={() => setDurationOpen(true)} aria-label={`${MODE_LABELS[timer.mode]} timer, ${formatClock(remainingMs)}. Change duration`} className="mt-8 rounded-2xl px-3 py-1 tabular text-6xl font-semibold tracking-tight text-ink transition-colors duration-150 hover:text-coral focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-coral sm:text-7xl" data-id="element-176" data-mp="uoyypzad96le">
          {formatClock(remainingMs)}
        </button>
        <p aria-live="polite" className="mt-2 h-5 text-xs text-muted" data-id="element-177" data-mp="w5w9gas2d64h">
          {message ?? `${MODE_LABELS[timer.mode]} · ${Math.round(totalMs / 60000)} min`}
        </p>

        {settings.dailyGoal !== null && <div className="mt-5 w-full" data-id="element-178" data-mp="i7o4bfkviyi8">
            <GoalLine minutes={todayMinutes} goal={settings.dailyGoal} reached={todayMinutes >= settings.dailyGoal} data-id="element-179" data-mp="i8ptenyq5liy" />
          </div>}

        <AnimatePresence data-id="element-180" data-mp="dfehw9iywgkp">
          {goalReached && <motion.p initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-3 text-xs font-medium text-coral" data-id="element-181" data-mp="3lvq389e11ela">
              Daily goal reached.
            </motion.p>}
        </AnimatePresence>

        <div className="mt-8 w-full" data-id="element-182" data-mp="1ulo0angu7hkl">
          <button type="button" onClick={() => {
          dismissMessage();
          start();
        }} className="min-h-[56px] w-full rounded-2xl bg-coral text-base font-semibold text-white transition-colors duration-150 hover:bg-coral-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral" data-id="element-183" data-mp="1umplhkrkhkg1">
            {primaryLabel}
          </button>
          <div className="mt-3 flex items-center justify-center" data-id="element-184" data-mp="1fwrrupavc2jg">
            <button type="button" onClick={handleReset} className="flex min-h-[44px] items-center gap-1.5 rounded-xl px-3 text-xs font-medium text-muted transition-colors duration-150 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral" data-id="element-185" data-mp="2mc14kxz50a6i">
              <RotateCcwIcon size={14} strokeWidth={1.75} aria-hidden="true" data-id="element-186" data-mp="2wghi3kp59f75" />
              Reset
            </button>
          </div>
        </div>

        <ul className="mt-6 flex items-center gap-2" aria-label={`${timer.completedInCycle} of ${settings.longBreakInterval} focus sessions in this cycle`} data-id="element-187" data-mp="2nnxgcbdk2ui3">
          {Array.from({
          length: settings.longBreakInterval
        }).map((_, i) => <li key={i} className={`h-1.5 w-1.5 rounded-full transition-colors duration-150 ${i < timer.completedInCycle ? 'bg-coral' : 'bg-line'}`} data-id="element-188" data-mp="2drko7hxnsopg" data-mpi={i} />)}
        </ul>

        <div className="mt-8 w-full" data-id="element-189" data-mp="6xqnkxgetptt">
          <label htmlFor="task" className="sr-only" data-id="element-190" data-mp="6wnxusutptfh">
            What are you focusing on?
          </label>
          <input id="task" type="text" value={task} onChange={e => setTask(e.target.value)} placeholder="What are you focusing on?" className="min-h-[44px] w-full rounded-xl border border-line bg-transparent px-4 text-center text-sm text-ink placeholder:text-muted focus:border-muted focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral" data-id="element-191" data-mp="2vc4qdcqqq2j" />
        </div>
      </div>

      <DurationSheet open={durationOpen} mode={timer.mode} value={settings.durations[timer.mode]} onClose={() => setDurationOpen(false)} onSelect={applyDuration} data-id="element-192" data-mp="7gsbig5mft23" />

      <SoundSheet open={soundOpen} onClose={() => setSoundOpen(false)} data-id="element-193" data-mp="3ul84moy73i6o" />

      <ConfirmDialog open={pendingMinutes !== null} title="Replace this session?" description="Changing the duration will restart the current session from the beginning." confirmLabel="Replace" onConfirm={() => {
      if (pendingMinutes !== null) setDuration(timer.mode, pendingMinutes);
      setPendingMinutes(null);
      setDurationOpen(false);
    }} onCancel={() => setPendingMinutes(null)} data-id="element-194" data-mp="3unkudoa72wua" />

      <ConfirmDialog open={pendingMode !== null} title="Leave this session?" description="Switching mode ends the session in progress without recording it." confirmLabel="Switch" onConfirm={() => {
      if (pendingMode) selectMode(pendingMode);
      setPendingMode(null);
    }} onCancel={() => setPendingMode(null)} data-id="element-195" data-mp="2bs6ma5qh8pw6" />

      <ConfirmDialog open={confirmReset} title="Reset the timer?" description="This session is in progress. Resetting clears the time already spent." confirmLabel="Reset" onConfirm={() => {
      reset();
      setConfirmReset(false);
    }} onCancel={() => setConfirmReset(false)} data-id="element-196" data-mp="2uo6k3x64ynno" />
    </div>;
}